<template>
  <!-- Header -->
  <header class="mv-header">
    <img :src="logoUrl" class="mv-logo" alt="ISBET" />
    <div class="mv-header-info">
      <span class="mv-header-name">{{ authUser?.name }}</span>
      <span class="mv-header-role">{{ authUser?.role === 'administrator' ? 'Administrador' : 'Agente' }}</span>
    </div>
    <button class="mv-icon-btn" @click="$emit('logout')" title="Sair">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
        <polyline points="16 17 21 12 16 7"/>
        <line x1="21" y1="12" x2="9" y2="12"/>
      </svg>
    </button>
  </header>

  <!-- Banner de dados desatualizados -->
  <div v-if="dataStale" class="mv-stale-bar">
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
    Sem conexão — dados desatualizados
    <button class="mv-stale-btn" @click="$emit('refresh')" :disabled="loading">Tentar</button>
  </div>

  <!-- Search -->
  <div class="mv-search-wrap">
    <svg class="mv-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
    <input
      :value="query"
      @input="$emit('update:query', ($event.target as HTMLInputElement).value)"
      class="mv-search"
      type="search"
      placeholder="Buscar conversa…"
    />
  </div>

  <!-- Section label -->
  <div class="mv-section-label">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
    Minhas conversas ativas
    <span class="mv-section-count">{{ filteredConvs.length }}</span>
    <div class="mv-period">
      <button
        v-for="opt in periodOptions"
        :key="opt.value"
        type="button"
        class="mv-period-btn"
        :class="{ active: periodo === opt.value }"
        :disabled="loading"
        @click="$emit('update:periodo', opt.value)"
      >{{ opt.label }}</button>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading && !hasConversations" class="mv-loading">
    <div class="mv-spinner"></div>
    <span>Carregando conversas…</span>
  </div>

  <!-- Empty -->
  <div v-else-if="!filteredConvs.length && !loading" class="mv-empty">
    <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
    <p>Nenhuma conversa aberta atribuída a você</p>
    <button class="mv-refresh-btn" @click="$emit('refresh')">Atualizar</button>
  </div>

  <!-- Conversation list -->
  <div v-else class="mv-list">
    <div
      v-for="conv in filteredConvs"
      :key="conv.id"
      class="mv-item"
      :class="{ 'has-unread': conv.unread_count > 0 }"
      @click="$emit('open-chat', conv)"
    >
      <div class="mv-item-av-wrap">
        <div class="mv-item-av" :style="{ background: avatarColor(conv.contact.name) }">
          <img
            v-if="conv.contact.thumbnail"
            :src="conv.contact.thumbnail"
            :alt="conv.contact.name"
            @error="(e) => (e.target as HTMLImageElement).style.display='none'"
          />
          <span v-else>{{ initials(conv.contact.name) }}</span>
        </div>
        <span class="mv-status-dot" :class="dotClass(conv)"></span>
      </div>

      <div class="mv-item-body">
        <div class="mv-item-r1">
          <span class="mv-item-name">{{ conv.contact.name }}</span>
          <span class="mv-item-time">{{ convTime(conv) }}</span>
        </div>
        <div class="mv-item-r2">
          <span class="mv-item-preview" :class="{ bold: conv.unread_count > 0 }">{{ previewText(conv.id) }}</span>
          <span v-if="conv.unread_count" class="mv-unread">{{ conv.unread_count > 9 ? '9+' : conv.unread_count }}</span>
        </div>
        <div class="mv-item-tags" v-if="conv.team || conv.labels.length || conv.status === 'snoozed'">
          <span v-if="conv.status === 'snoozed'" class="mv-tag mv-tag-snoozed">Adiada</span>
          <span v-if="conv.team" class="mv-tag mv-tag-team">{{ conv.team.name }}</span>
          <span v-for="l in conv.labels.slice(0, 2)" :key="l" class="mv-tag">{{ l.replace(/_/g, ' ') }}</span>
        </div>
      </div>

      <svg class="mv-item-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="9 18 15 12 9 6"/>
      </svg>
    </div>
  </div>

  <!-- Footer bar -->
  <div class="mv-footer-bar">
    <span>Atualiza em {{ countdown }}s</span>
    <button class="mv-refresh-sm" @click="$emit('refresh')" :disabled="loading">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" :class="{ 'spin': loading }">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
      Atualizar
    </button>
  </div>
</template>

<script setup lang="ts">
import type { Conversation } from '@/types/dashboard'
import { avatarColor, initials } from '@/composables/useHelpers'
import { dotClass, convTime } from '@/utils/mobileFormat'
import type { VisaoGeralPeriodo } from '@/composables/useDashboardEngine'

defineProps<{
  authUser: { name?: string; role?: string } | null
  dataStale: boolean
  loading: boolean
  hasConversations: boolean
  countdown: number
  query: string
  filteredConvs: Conversation[]
  logoUrl: string
  previewText: (convId: number) => string
  periodo: VisaoGeralPeriodo
}>()

defineEmits<{
  'update:query': [value: string]
  'update:periodo': [value: VisaoGeralPeriodo]
  logout: []
  refresh: []
  'open-chat': [conv: Conversation]
}>()

// Mesmas duas opções/labels do toggle "Visão Geral" do desktop (KpiGrid.vue)
// — período aqui filtra a própria lista de conversas do mobile, não só um
// KPI, mas o rótulo/comportamento (Esta semana/Mês) é o mesmo em espírito.
const periodOptions: { value: VisaoGeralPeriodo; label: string }[] = [
  { value: 'week',  label: 'Semana' },
  { value: 'month', label: 'Mês' },
]
</script>

<style scoped>
/* ══ Header ══ */
.mv-header {
  background: #021627;
  border-bottom: 2px solid #BC0C24;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: max(12px, env(safe-area-inset-top));
}

.mv-logo {
  width: 38px; height: 38px;
  border-radius: 10px;
  object-fit: contain;
  background: rgba(255,255,255,.08);
  padding: 4px;
  flex-shrink: 0;
}

.mv-header-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.mv-header-name {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}
.mv-header-role {
  font-size: 10.5px;
  color: rgba(255,255,255,.45);
  text-transform: capitalize;
}

/* Compartilhado com o header do chat (MobileChatHeader) */
.mv-icon-btn {
  background: rgba(255,255,255,.08);
  border: none;
  color: rgba(255,255,255,.7);
  width: 36px; height: 36px;
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s, color .15s;
  text-decoration: none;
}
.mv-icon-btn:hover, .mv-icon-btn:active { background: rgba(255,255,255,.16); color: #fff; }

/* ══ Stale bar ══ */
.mv-stale-bar {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  padding: 7px 14px;
  font-size: 11.5px;
  font-weight: 600;
  color: #b91c1c;
  flex-shrink: 0;
}
.mv-stale-bar svg { flex-shrink: 0; }
.mv-stale-btn {
  margin-left: auto;
  background: #b91c1c;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  flex-shrink: 0;
}
.mv-stale-btn:disabled { opacity: .5; }

/* ══ Search ══ */
.mv-search-wrap {
  background: #fff;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #E5E7EB;
  flex-shrink: 0;
}
.mv-search-ico {
  width: 15px; height: 15px;
  color: #9CA3AF;
  flex-shrink: 0;
}
.mv-search {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  font-family: 'Poppins', sans-serif;
  color: #1A1A1A;
  background: transparent;
}
.mv-search::placeholder { color: #9CA3AF; }

/* ══ Section label ══ */
.mv-section-label {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #6B6B6B;
  background: #F7F7F7;
  flex-shrink: 0;
}
.mv-section-count {
  background: rgba(188,12,36,.1);
  color: #BC0C24;
  padding: 1px 7px;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 800;
}

.mv-period {
  margin-left: auto;
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}
.mv-period-btn {
  border: 1px solid #E5E7EB;
  background: #fff;
  color: #6B6B6B;
  font-size: 10px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  padding: 3px 9px;
  border-radius: 20px;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  transition: background .15s, color .15s, border-color .15s;
}
.mv-period-btn.active {
  background: #BC0C24;
  border-color: #BC0C24;
  color: #fff;
}
.mv-period-btn:disabled { opacity: .5; }

/* ══ Loading ══ */
.mv-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #6B6B6B;
  font-size: 13px;
}

/* Compartilhado com outras telas/sheets do Mobile */
.mv-spinner {
  width: 32px; height: 32px;
  border: 3px solid #E5E7EB;
  border-top-color: #BC0C24;
  border-radius: 50%;
  animation: spin .8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ══ Empty ══ */
.mv-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9CA3AF;
  padding: 32px;
  text-align: center;
}
.mv-empty p { font-size: 14px; color: #6B6B6B; }
.mv-refresh-btn {
  background: #BC0C24;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
}

/* ══ Conversation list ══ */
.mv-list {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
}

.mv-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #F3F4F6;
  cursor: pointer;
  transition: background .12s;
  -webkit-tap-highlight-color: transparent;
}
.mv-item:active { background: #F7F7F7; }
.mv-item.has-unread { background: rgba(188,12,36,.03); }

/* Compartilhado com o header do chat (MobileChatHeader) */
.mv-item-av-wrap {
  position: relative;
  flex-shrink: 0;
}
.mv-item-av {
  width: 46px; height: 46px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 800;
  color: #fff; overflow: hidden;
}
.mv-item-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }

/* Compartilhado com o header do chat (MobileChatHeader) */
.mv-status-dot {
  position: absolute;
  bottom: 1px; right: 1px;
  width: 13px; height: 13px;
  border-radius: 50%;
  border: 2.5px solid #fff;
}
.mv-status-dot.sm { width: 11px; height: 11px; }
.mv-status-dot.dot-new     { background: #22c55e; animation: pulse-dot 1.8s ease-in-out infinite; }
.mv-status-dot.dot-pending { background: #f97316; }
.mv-status-dot.dot-open    { background: #3b82f6; }
.mv-status-dot.dot-snoozed { background: #d97706; }

@keyframes pulse-dot {
  0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  50%      { box-shadow: 0 0 0 4px rgba(34,197,94,0); }
}

.mv-item-body {
  flex: 1;
  min-width: 0;
}
.mv-item-r1 {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 2px;
}
.mv-item-name {
  font-size: 14px;
  font-weight: 700;
  color: #1A1A1A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 62%;
}
.mv-item-time {
  font-size: 11px;
  color: #9CA3AF;
  flex-shrink: 0;
}
.mv-item-r2 {
  display: flex;
  align-items: center;
  gap: 6px;
}
.mv-item-preview {
  font-size: 12.5px;
  color: #6B6B6B;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}
.mv-item-preview.bold { font-weight: 700; color: #1A1A1A; }
.mv-unread {
  background: #BC0C24;
  color: #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  min-width: 18px; height: 18px;
  line-height: 18px;
  text-align: center;
  padding: 0 4px;
  flex-shrink: 0;
}
.mv-item-tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.mv-tag {
  font-size: 10px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 20px;
  background: rgba(3,105,161,.1);
  color: #0369A1;
  white-space: nowrap;
}
.mv-tag-team {
  background: rgba(188,12,36,.08);
  color: #BC0C24;
}
.mv-tag-snoozed { background: rgba(217,119,6,.1); color: #92400e; }
.mv-item-chevron {
  color: #D1D5DB;
  flex-shrink: 0;
}

/* ══ Footer bar ══ */
.mv-footer-bar {
  background: #fff;
  border-top: 1px solid #E5E7EB;
  padding: 8px 16px;
  padding-bottom: max(8px, env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.mv-footer-bar span {
  font-size: 11.5px;
  color: #9CA3AF;
}
.mv-refresh-sm {
  display: flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid #E5E7EB;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 11.5px;
  color: #6B6B6B;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
}
.mv-refresh-sm:disabled { opacity: .5; }
.spin { animation: spin .7s linear infinite; }
</style>
